import { snapdom } from '@zumer/snapdom';

// Max scale to Full HD (1920x1080 standard)
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;

/**
 * Calculate scaled dimensions to maximize both width and height while staying within limits
 */
function getScaledDimensions(
  originalWidth: number,
  originalHeight: number,
): { width: number; height: number } {
  if (originalWidth <= MAX_WIDTH && originalHeight <= MAX_HEIGHT) {
    return { width: originalWidth, height: originalHeight };
  }

  const widthScale = MAX_WIDTH / originalWidth;
  const heightScale = MAX_HEIGHT / originalHeight;

  // Use the smaller scale factor to ensure both dimensions fit within limits
  const scale = Math.min(widthScale, heightScale);

  return {
    width: Math.round(originalWidth * scale),
    height: Math.round(originalHeight * scale),
  };
}

// Function to capture a screenshot of the entire page
async function captureScreenshot(domBody: HTMLElement): Promise<string> {
  const originalHeight = document.documentElement.scrollHeight;
  const originalWidth = document.documentElement.scrollWidth;
  // Scale dimensions to keep file size reasonable
  const { width, height } = getScaledDimensions(originalWidth, originalHeight);
  const styles = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join('');
      } catch (e) {
        // Error accessing stylesheet rules. Nothing we can do about it.
        return '';
      }
    })
    .join('\n');

  // Create a new style element with all the rules
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  domBody.appendChild(styleElement);

  try {
    const canvas = await snapdom.toCanvas(domBody, {
      quality: 0.7,
      width,
      height,
      exclude: ['.bf-nocapture'],
    });

    return canvas.toDataURL('image/webp', 0.7);
  } finally {
    domBody.removeChild(styleElement);
  }
}

export { captureScreenshot };
